import { version } from '../version';

const VERSION_CHECK_INTERVAL = 60000; // Check every 1 minute
const STORAGE_KEY = 'app_version';

export class VersionChecker {
  constructor() {
    this.currentVersion = version;
    this.checkInterval = null;
  }

  // Start checking for version updates
  startVersionCheck() {
    console.log('startVersionCheck called');
    
    // Add flag to prevent multiple instances
    if (window.versionCheckerActive) {
      console.log('Version checker already active, skipping');
      return;
    }
    window.versionCheckerActive = true;

    // Store current version in localStorage on first load
    const storedVersion = localStorage.getItem(STORAGE_KEY);
    console.log('Version check:', { current: this.currentVersion, stored: storedVersion });
    
    if (!storedVersion) {
      localStorage.setItem(STORAGE_KEY, this.currentVersion);
      console.log('Stored initial version:', this.currentVersion);
    } else if (storedVersion !== this.currentVersion) {
      // Check if we just updated (prevent infinite refresh loop)
      const lastUpdateTime = localStorage.getItem('last_version_update');
      const now = Date.now();
      
      console.log('Version mismatch detected:', { stored: storedVersion, current: this.currentVersion, lastUpdate: lastUpdateTime });
      
      // Only force update if it's been more than 1 minute since last update
      if (!lastUpdateTime || (now - parseInt(lastUpdateTime)) > 60000) {
        localStorage.setItem('last_version_update', now.toString());
        console.log('Forcing update due to version mismatch');
        this.forceUpdate();
        return;
      } else {
        // Update stored version to current to prevent loop
        localStorage.setItem(STORAGE_KEY, this.currentVersion);
        console.log('Updated stored version to prevent loop');
      }
    }

    // Start periodic checks (but less frequent to avoid issues)
    console.log(`Starting periodic checks every ${VERSION_CHECK_INTERVAL}ms`);
    this.checkInterval = setInterval(() => {
      this.checkForUpdates();
    }, VERSION_CHECK_INTERVAL);
  }

  // Stop version checking
  stopVersionCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    window.versionCheckerActive = false;
  }

  // Check for version updates using service worker cache version
  async checkForUpdates() {
    try {
      // Check if we recently updated to avoid rapid checks
      const lastUpdateTime = localStorage.getItem('last_version_update');
      const now = Date.now();
      
      if (lastUpdateTime && (now - parseInt(lastUpdateTime)) < 30000) {
        // Skip check if we updated less than 30 seconds ago
        return;
      }

      // Check service worker cache version instead of version.js
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const messageChannel = new MessageChannel();
        
        return new Promise((resolve) => {
          messageChannel.port1.onmessage = (event) => {
            if (event.data && event.data.version) {
              const serverCacheVersion = event.data.version;
              const storedCacheVersion = localStorage.getItem(STORAGE_KEY + '_cache');
              
              if (storedCacheVersion && serverCacheVersion !== storedCacheVersion) {
                console.log(`Cache version update detected: ${storedCacheVersion} -> ${serverCacheVersion}`);
                localStorage.setItem('last_version_update', now.toString());
                localStorage.setItem(STORAGE_KEY + '_cache', serverCacheVersion);
                this.forceUpdate();
              } else if (!storedCacheVersion) {
                localStorage.setItem(STORAGE_KEY + '_cache', serverCacheVersion);
              }
            }
            resolve();
          };

          navigator.serviceWorker.controller.postMessage(
            { type: 'GET_VERSION' }, 
            [messageChannel.port2]
          );
        });
      }
    } catch (error) {
      console.warn('Version check failed:', error);
    }
  }

  // Force update by clearing caches and reloading
  async forceUpdate() {
    console.log('Forcing application update...');
    
    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('All caches cleared');
      }

      // Update stored version
      localStorage.setItem(STORAGE_KEY, this.currentVersion);
      
      // Unregister service worker if it exists
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (let registration of registrations) {
          await registration.unregister();
        }
      }

      // Show update notification to user
      this.showUpdateNotification();
      
    } catch (error) {
      console.error('Error during forced update:', error);
      // Force reload anyway
      window.location.reload(true);
    }
  }

  // Show update notification and reload
  showUpdateNotification() {
    const shouldReload = window.confirm(
      'A new version of the application is available. The page will reload to update.'
    );
    
    if (shouldReload) {
      // Force a hard reload
      window.location.reload(true);
    } else {
      // User declined, but we'll reload in 5 seconds anyway
      setTimeout(() => {
        window.location.reload(true);
      }, 5000);
    }
  }

  // Manual version check
  checkNow() {
    return this.checkForUpdates();
  }
}

// Create singleton instance
export const versionChecker = new VersionChecker();

// Auto-start version checking when module loads (enabled in all environments for testing)
if (typeof window !== 'undefined') {
  console.log('Version checker initializing...', {
    environment: process.env.NODE_ENV,
    hostname: window.location.hostname,
    currentVersion: version
  });
  
  // Start after a longer delay to ensure app is fully loaded
  setTimeout(() => {
    console.log('Starting version checker...');
    versionChecker.startVersionCheck();
  }, 10000);
}